// pages/analysis/HealthAnalysis.js —— 分析中心 · 健康度分析（doc01 2.14，缺陷 96/97/98）
// 结构：顶部查询卡片（两行：站点名称/资产名称 模糊查询 + 是否为预测值/资产寿命/运行效率/安全性/可靠性 枚举下拉 + 查询/重置）
//       → 最新更新时间行（每月 1 号更新）→ 列表（站点名称 / 资产编号 / 资产名称 / 出厂日期 / 是否配置预测值 /
//       资产寿命 / 运行效率 / 可靠性指标 / 安全性指标 / 操作）。
// 四个指标为列表上 4 个独立单元格，按「实际值 vs 预测值」超出阈值着色预警（缺陷 98）。
// 「详情」按钮进入二级页面 /app/analysis/health/:id 查看该资产当年 4 指标实测 vs 预测对比曲线（缺陷 97：不再堆在列表底部）。
import React, { useState } from 'react';
import { Table, Select, Input, Button, Tag } from 'antd';
import { useNavigate } from 'react-router-dom';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import PageHead from '../../components/PageHead';
import { HEALTH_ASSETS } from '../../config/stationData';

// 最新更新时间（每月 1 号由定时任务更新，具体时间以定时任务结束为准）
const LAST_UPDATE = '2026-10-01 02:18';

// 枚举筛选：默认「全部」
const ALL = { value: '', label: '全部' };
const GRADE_OPTIONS = [ALL, ...['优', '良', '中', '差'].map((v) => ({ value: v, label: v }))];
const CFG_OPTIONS = [ALL, { value: '已配置', label: '已配置' }, { value: '未配置', label: '未配置' }];

// 四指标的等级区间（用于「资产寿命/运行效率/安全性/可靠性」枚举筛选的真实过滤）
const GRADE_BANDS = {
  life: [[96, '优'], [94, '良'], [92, '中']],
  eff: [[94, '优'], [92, '良'], [90, '中']],
  safety: [[97, '优'], [95.5, '良'], [94, '中']],
  reliability: [[96.5, '优'], [95, '良'], [93.5, '中']],
};
const gradeOf = (metric, v) => {
  if (typeof v !== 'number') return '';
  const bands = GRADE_BANDS[metric];
  for (const [min, g] of bands) if (v >= min) return g;
  return '差';
};

// 预警规则：寿命 / 效率低于预测值预警；安全性 / 可靠性高于预测值预警
const WARN_DIR = { life: 'low', eff: 'low', safety: 'high', reliability: 'high' };
const isWarn = (metric, actual, pred) => {
  if (typeof actual !== 'number' || typeof pred !== 'number') return false;
  return WARN_DIR[metric] === 'low' ? actual < pred : actual > pred;
};

const EMPTY = { site: '', asset: '', cfg: '', life: '', eff: '', safety: '', reliability: '' };

const matchFilters = (a, f) => {
  if (f.site && !a.site.toLowerCase().includes(f.site.toLowerCase().trim())) return false;
  if (f.asset && !a.name.toLowerCase().includes(f.asset.toLowerCase().trim())) return false;
  if (f.cfg && a.predictCfg !== f.cfg) return false;
  if (f.life && gradeOf('life', a.life) !== f.life) return false;
  if (f.eff && gradeOf('eff', a.eff) !== f.eff) return false;
  if (f.safety && gradeOf('safety', a.safety) !== f.safety) return false;
  if (f.reliability && gradeOf('reliability', a.reliability) !== f.reliability) return false;
  return true;
};

// 单个指标单元格：数值 + 状态（超出预测值阈值 → 预警，橙色/红色；否则正常，绿色）
const WARN_COLOR = '#ff5a7f';
const OK_COLOR = '#52e8b0';
const MetricCell = ({ value, warn }) => {
  if (typeof value !== 'number') return <span style={{ color: 'rgba(255,255,255,0.3)' }}>—</span>;
  const color = warn ? WARN_COLOR : OK_COLOR;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <b style={{ color, fontVariantNumeric: 'tabular-nums' }}>{value}%</b>
      <span style={{ color, fontSize: 12 }}>{warn ? '预警' : '正常'}</span>
    </span>
  );
};

const HealthAnalysis = () => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);

  const setField = (k, v) => setDraft((s) => ({ ...s, [k]: v }));
  const doQuery = () => setApplied({ ...draft });
  const doReset = () => { setDraft(EMPTY); setApplied(EMPTY); };

  const rows = HEALTH_ASSETS.filter((a) => matchFilters(a, applied));

  const columns = [
    { title: '站点名称', dataIndex: 'site', width: 150 },
    { title: '资产编号', dataIndex: 'code', width: 130, render: (v) => <span style={{ color: '#00d4ff' }}>{v}</span> },
    { title: '资产名称', dataIndex: 'name', width: 120 },
    { title: '出厂日期', dataIndex: 'factory', width: 120 },
    {
      title: '是否配置预测值', dataIndex: 'predictCfg', width: 130,
      render: (v) => <Tag color={v === '已配置' ? 'green' : 'default'} style={{ margin: 0 }}>{v}</Tag>,
    },
    { title: '资产寿命', dataIndex: 'life', width: 140, render: (v, r) => <MetricCell value={v} warn={isWarn('life', r.life, r.lifePred)} /> },
    { title: '运行效率', dataIndex: 'eff', width: 140, render: (v, r) => <MetricCell value={v} warn={isWarn('eff', r.eff, r.effPred)} /> },
    { title: '可靠性指标', dataIndex: 'reliability', width: 140, render: (v, r) => <MetricCell value={v} warn={isWarn('reliability', r.reliability, r.reliabilityPred)} /> },
    { title: '安全性指标', dataIndex: 'safety', width: 140, render: (v, r) => <MetricCell value={v} warn={isWarn('safety', r.safety, r.safetyPred)} /> },
    {
      title: '操作', key: 'op', width: 90, fixed: 'right',
      render: (_, r) => (
        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(`/app/analysis/health/${r.key}`)}>详情</Button>
      ),
    },
  ];

  return (
    <div>
      <PageHead title="健康度分析" desc="按站点 / 资产查询，对预测值配置的资产对比寿命 / 效率 / 安全性 / 可靠性四指标的实测值与预测值" />

      {/* 顶部查询卡片：两行 */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="filter-bar-fields" style={{ marginBottom: 12 }}>
          <div className="filter-item">
            <span className="filter-label">站点名称</span>
            <Input value={draft.site} onChange={(e) => setField('site', e.target.value)} placeholder="模糊查询" prefix={<SearchOutlined />} style={{ width: 180 }} allowClear />
          </div>
          <div className="filter-item">
            <span className="filter-label">资产名称</span>
            <Input value={draft.asset} onChange={(e) => setField('asset', e.target.value)} placeholder="模糊查询" prefix={<SearchOutlined />} style={{ width: 180 }} allowClear />
          </div>
          <div className="filter-item">
            <span className="filter-label">是否配置预测值</span>
            <Select value={draft.cfg} onChange={(v) => setField('cfg', v)} options={CFG_OPTIONS} style={{ width: 150 }} />
          </div>
          <div className="filter-item">
            <span className="filter-label">资产寿命</span>
            <Select value={draft.life} onChange={(v) => setField('life', v)} options={GRADE_OPTIONS} style={{ width: 110 }} />
          </div>
        </div>
        <div className="filter-bar-fields">
          <div className="filter-item">
            <span className="filter-label">运行效率</span>
            <Select value={draft.eff} onChange={(v) => setField('eff', v)} options={GRADE_OPTIONS} style={{ width: 110 }} />
          </div>
          <div className="filter-item">
            <span className="filter-label">安全性</span>
            <Select value={draft.safety} onChange={(v) => setField('safety', v)} options={GRADE_OPTIONS} style={{ width: 110 }} />
          </div>
          <div className="filter-item">
            <span className="filter-label">可靠性</span>
            <Select value={draft.reliability} onChange={(v) => setField('reliability', v)} options={GRADE_OPTIONS} style={{ width: 110 }} />
          </div>
          <div className="filter-bar-actions">
            <Button type="primary" icon={<SearchOutlined />} onClick={doQuery}>查询</Button>
            <Button icon={<ReloadOutlined />} onClick={doReset} style={{ marginLeft: 8 }}>重置</Button>
          </div>
        </div>
      </div>

      {/* 最新更新时间 */}
      <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginBottom: 10 }}>
        最新更新时间：{LAST_UPDATE}（每月 1 号更新，具体时间以定时任务结束为准）
      </div>

      <div className="panel">
        <Table
          rowKey="key"
          dataSource={rows}
          columns={columns}
          size="middle"
          scroll={{ x: 1200 }}
          pagination={{ pageSize: 16, size: 'small' }}
        />
      </div>
    </div>
  );
};

export default HealthAnalysis;
